import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const Lightbox = ({ images, isOpen, onClose, initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        previousImage();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        nextImage();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, currentIndex, images.length]); // Add dependencies

  const nextImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const previousImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onClose();
  };

  const handleBackgroundClick = (e) => {
    // Only close if clicking on the background, not on child elements
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen || !images || images.length === 0) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center"
      onClick={handleBackgroundClick}
    >
      {/* Close Button */}
      <button
        onClick={handleClose}
        className="absolute top-4 right-4 z-60 p-3 text-white hover:text-gray-300 transition-colors duration-200 bg-black bg-opacity-50 rounded-full"
        style={{ zIndex: 1000 }}
      >
        <X className="h-6 w-6" />
      </button>

      {/* Navigation Buttons */}
      {images.length > 1 && (
        <>
          <button
            onClick={previousImage}
            className="absolute left-4 z-60 p-3 text-white hover:text-gray-300 transition-colors duration-200 bg-black bg-opacity-50 rounded-full hover:bg-opacity-75"
            style={{ zIndex: 1000 }}
          >
            <ChevronLeft className="h-8 w-8" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-4 z-60 p-3 text-white hover:text-gray-300 transition-colors duration-200 bg-black bg-opacity-50 rounded-full hover:bg-opacity-75"
            style={{ zIndex: 1000 }}
          >
            <ChevronRight className="h-8 w-8" />
          </button>
        </>
      )}

      {/* Main Image Container */}
      <div 
        className="max-w-7xl max-h-full mx-4 flex items-center justify-center z-50 relative"
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on image area
      >
        <img
          src={images[currentIndex]}
          alt={`Image ${currentIndex + 1}`}
          className="max-w-full max-h-full object-contain"
          style={{ maxHeight: '90vh' }}
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      {/* Image Counter */}
      {images.length > 1 && (
        <div 
          className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-60 bg-black bg-opacity-75 px-4 py-2 rounded-full"
          style={{ zIndex: 1000 }}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="text-white text-sm font-medium">
            {currentIndex + 1} / {images.length}
          </span>
        </div>
      )}

      {/* Thumbnail Strip */}
      {images.length > 1 && (
        <div 
          className="absolute bottom-16 left-1/2 transform -translate-x-1/2 z-60 flex space-x-2 max-w-full overflow-x-auto bg-black bg-opacity-50 p-3 rounded-lg"
          style={{ zIndex: 1000 }}
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((image, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCurrentIndex(index);
              }}
              className={`flex-shrink-0 w-16 h-16 border-2 transition-all duration-200 rounded ${
                index === currentIndex
                  ? 'border-white opacity-100'
                  : 'border-transparent opacity-60 hover:opacity-80 hover:border-gray-400'
              }`}
            >
              <img
                src={image}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover rounded"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Lightbox;